import { BigNumber } from 'ethers'
import { Trophy } from '~/modules/Trophies/Domain/Trophy'
import {
  ApiTrophyAnimationResponseInterface,
  ApiTrophyResponseInterface,
  TrophyMemoryInterface
} from './ApiTrophyResponseInterface'
import ApiTrophyResponseTranslatorException from '~/modules/Trophies/Application/ApiTrophyResponseTranslatorException'
import Relation from '~/modules/Relations/Domain/Relation'
import { Organizer } from '~/modules/Organizers/Domain/Organizer'
import { TrophyMemory } from '~/modules/TrophyMemories/Domain/TrophyMemory'
import { FileUrlParser } from '~/modules/TrophyMemories/Domain/FileUrlParser'
import { DateTime } from 'luxon'
import { Collection } from '~/modules/Products/Domain/Collection'

export default class ApiTrophyResponseTranslator {
  public static responseFromDomain (trophy: Trophy): ApiTrophyResponseInterface {
    if (!trophy.id) {
      throw ApiTrophyResponseTranslatorException.trophyDoesNotHaveId(trophy)
    }

    let trophyMemories: TrophyMemory[] = []

    try {
      trophyMemories = trophy.memories
    } catch (exception: unknown) {
      console.warn(exception)
    }

    let trophyAnimation: ApiTrophyAnimationResponseInterface | null = null

    if (trophy.animationUrl !== null) {
      trophyAnimation = {
        type: trophy.getAnimationUrlVideoType(),
        value: trophy.animationUrl,
      }
    }

    let date: string | null = null

    if (trophy.date !== null) {
      date = trophy.date.toISO()
    }

    let collection: Collection | null = null

    try {
      collection = trophy.collection
    } catch (exception: unknown) { }

    return {
      id: trophy.id.toString(),
      collectionId: trophy.collectionId,
      tokenId: trophy.tokenId ? trophy.tokenId.toString() : null,
      name: trophy.name,
      description: trophy.description,
      image: trophy.image,
      attributes: trophy.attributes,
      mintStatus: trophy.mintStatus,
      animation: trophyAnimation,
      rawImageUrl: trophy.rawImageUrl,
      trophyMemories: trophyMemories.map(ApiTrophyResponseTranslator.trophyMemoryFromDomain),
      userId: trophy.userId,
      organizer: trophy.organizer.toJSON(),
      ...(collection && { collection: collection.toJSON() }),
      date,
    }
  }

  public static domainFromResponse (response: ApiTrophyResponseInterface): Trophy {
    const trophy = new Trophy(
      BigNumber.from(response.id),
      response.collectionId,
      BigNumber.from(response.tokenId),
      response.name,
      response.description,
      response.image,
      response.attributes,
      response.mintStatus,
      response.animation?.value ?? null,
      Relation.from(response.trophyMemories.map(ApiTrophyResponseTranslator.trophyMemoryToDomain)),
      response.rawImageUrl,
      response.organizer.id,
      response.userId,
      null,
      null,
      null,
      null
    )

    trophy.setOrganizer(
      new Organizer(
        response.organizer.id,
        response.organizer.name,
        response.organizer.avatarUrl,
        response.organizer.bannerUrl,
        response.organizer.logoUrl,
        response.organizer.featuredTrophyId === null ? null : BigNumber.from(response.organizer.featuredTrophyId),
        response.organizer.place,
        response.organizer.brandHexColor
      )
    )

    if (response.collection) {
      trophy.setCollection(
        new Collection(
          response.collection.id,
          response.collection.contractAddress,
          response.collection.contractChain,
          '',
          response.collection.seriesId,
          response.collection.type
        )
      )
    }

    return trophy
  }

  private static trophyMemoryFromDomain (memory: TrophyMemory): TrophyMemoryInterface {
    return {
      id: memory.id,
      trophyId: memory.trophyId.toString(),
      type: memory.type,
      createdAt: memory.createdAt.toISO(),
      mediaUrl: memory.mediaUrl,
      thumbnailUrl: memory.thumbnailUrl,
    }
  }

  private static trophyMemoryToDomain (memory: TrophyMemoryInterface): TrophyMemory {
    return new TrophyMemory(
      memory.id,
      BigNumber.from(memory.trophyId),
      memory.type,
      FileUrlParser.parseFrom(memory.mediaUrl),
      FileUrlParser.parseFrom(memory.thumbnailUrl),
      DateTime.fromISO(memory.createdAt)
    )
  }
}
